// bd-deepcontext-b4zw — composition shell for the Scheduled Jobs page. What used
// to be a 2750-line component is now: data + 10s polling in useScheduledJobsData,
// mutations in useJobMutations, form state in useJobFormState, the runs modal in
// useJobRunsModal, pure logic in the sibling .ts modules and the render tree in
// JobsToolbar / JobRow / ScheduledJobFormModal. This file only wires them up.
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import JobRunsModal from "../jobRuns/JobRunsModal";
import SiteListModal from "../SiteListModal";
import Pagination from "../ui/pagination";
import { TableFillBox, fillOrHugClass } from "../ui/tableFillBox";
import { JobsAccessDenied, JobsAuthLoading } from "./AccessStates";
import JobsTable from "./JobsTable";
import JobsToolbar from "./JobsToolbar";
import ScheduledJobFormModal from "./ScheduledJobFormModal";
import { scheduledJobsApi } from "./api";
import {
  calculateDuration,
  formatDate,
  rel,
  relFuture,
  shortDate,
} from "./formatters";
import {
  buildPaginationInfo,
  JOBS_PER_PAGE,
  paginateJobs,
  selectVisibleJobs,
  sortJobsForDisplay,
} from "./jobList";
import { getRunStatusColor } from "./jobStatus";
import { getRunStatusIcon } from "./statusIcons";
import { useBlueprintUsage } from "./useBlueprintUsage";
import { useDebouncedValue } from "./useDebouncedValue";
import { useJobFormState } from "./useJobFormState";
import { useJobMutations } from "./useJobMutations";
import { useJobRunsModal } from "./useJobRunsModal";
import { usePoolEstimate } from "./usePoolEstimate";
import { useScheduledJobsData } from "./useScheduledJobsData";
import { isLocalModelAllowed } from "./localModelAccess";
import { useLocalModels } from "./useLocalModels";

const ScheduledJobsPage: React.FC = () => {
  const { user, impersonation, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // bd-sh26v — deep-link isolation: a Brand Blueprints connected-job pill links
  // here with ?jobId=<id>, isolating the list to that single job. This is a
  // DEDICATED exact-id filter, distinct from the bd-yj2sq text search (which
  // matches title/blueprint-name only, never job id). Seeded from the URL on
  // mount (mirrors AnalysisHistory's searchParams.get initializer).
  const [isolatedJobId, setIsolatedJobId] = useState<string>(
    () => searchParams.get("jobId") || "",
  );
  // bd-yj2sq — client-side search over job title + referenced blueprint names,
  // debounced like the Brand Blueprints search. No backend change.
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showSiteListModal, setShowSiteListModal] = useState(false);

  const form = useJobFormState({ onError: setError });
  const { jobs, stats, blueprintNames, loading, setLoading, reload } =
    useScheduledJobsData({ onError: setError });
  const blueprintUsage = useBlueprintUsage(form.isFormOpen);
  // deepcontext-7px2.4 / 63k8.3 — the local-model picker is gated to one user,
  // never while impersonated; only then is the server list fetched (the
  // endpoint 403s for everyone else).
  const localModelAllowed = isLocalModelAllowed(user, impersonation);
  const localModels = useLocalModels(localModelAllowed);
  const { poolEstimate, estimateLoading } = usePoolEstimate(
    form.formData,
    form.isFormOpen,
  );
  const mutations = useJobMutations({
    blueprintUsage,
    includeLocalModel: localModelAllowed,
    reload,
    setLoading,
    setError,
    setSuccess,
  });
  const runsModal = useJobRunsModal({ setLoading, setError, setSuccess });

  // bd-uhi3b — reset to page 1 whenever the filtered set changes (search or
  // deep-link isolation), so the visible page can never point past the end.
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, isolatedJobId]);

  const handleCreateJob = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.reportInvalid("create")) return;
    if (await mutations.createJob(form.formData)) form.finishCreate();
  };

  const handleUpdateJob = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.editingJob) return;
    if (form.reportInvalid("edit")) return;
    if (await mutations.updateJob(form.editingJob.id, form.formData)) {
      form.closeEditModal();
    }
  };

  const handleDuplicateJob = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.duplicateSourceJob) return;
    if (form.reportInvalid("duplicate")) return;
    if (
      await mutations.createJob(form.formData, {
        duplicateOfJobId: form.duplicateSourceJob.id,
        duplicateSourceName: form.duplicateSourceJob.name,
      })
    ) {
      form.closeDuplicateModal();
    }
  };

  // bd-wxgk9 / bd-tkpeg — deep-link to the Brand Blueprints page isolated to one
  // blueprint. Uses the blueprint ID (names are not unique) via a DEDICATED
  // ?blueprintId= param (mirrors this page's own ?jobId= isolation). PromptManager
  // reads it on mount, filters to that one blueprint, and swaps the search input
  // for a "Showing one blueprint" banner.
  const goToBlueprint = (promptId: string) => {
    navigate(`/prompts?blueprintId=${encodeURIComponent(promptId)}`);
  };

  // bd-sh26v — clear the isolation: drop local state and strip the URL param.
  const clearIsolation = () => {
    setIsolatedJobId("");
    const next = new URLSearchParams(searchParams);
    next.delete("jobId");
    setSearchParams(next, { replace: true });
  };

  const { isolatedJob, filteredJobs } = useMemo(
    () =>
      selectVisibleJobs({
        jobs,
        isolatedJobId,
        search: debouncedSearchTerm,
        blueprintNames,
      }),
    [jobs, isolatedJobId, debouncedSearchTerm, blueprintNames],
  );
  const sortedJobs = useMemo(
    () => sortJobsForDisplay(filteredJobs),
    [filteredJobs],
  );
  const paginationInfo = buildPaginationInfo({
    totalCount: sortedJobs.length,
    page: currentPage,
    limit: JOBS_PER_PAGE,
  });
  // n3uyh fill/compress: a full page fills the scroll area to the bottom; a
  // sparse/filtered page hugs its content.
  const isFullPage = sortedJobs.length >= JOBS_PER_PAGE;
  const pagedJobs = paginateJobs(sortedJobs, currentPage, JOBS_PER_PAGE);

  // Show loading while auth is being checked
  if (authLoading || user === null) return <JobsAuthLoading />;

  // bd-deepcontext-dnjks — staff (admin | adops | eskimi_admin) may open the
  // page and View Runs. Only admins (and not while impersonating a lower role)
  // may create / toggle / execute / edit / delete; every mutating control is
  // hidden for non-admins. Backend RBAC is the real guard; this is UX gating.
  const effectiveRole = user?.role;
  const isStaff =
    effectiveRole === "admin" ||
    effectiveRole === "adops" ||
    effectiveRole === "eskimi_admin";
  const isAdmin = effectiveRole === "admin" && !impersonation?.active;

  if (!isStaff) return <JobsAccessDenied role={user?.role} />;

  const formModalProps = {
    formData: form.formData,
    onChange: form.patchFormData,
    onCountryCodesChange: form.setCountryCodes,
    countriesError: form.countriesError,
    blueprintUsage,
    blueprintNames,
    onSelectedBlueprintCountriesChange: form.syncBlueprintCountries,
    selectedDomainInfos: form.selectedDomainInfos,
    onOpenSiteListModal: () => setShowSiteListModal(true),
    poolEstimate,
    estimateLoading,
    loading,
    showLocalModelPicker: localModelAllowed,
    localModels,
  };

  return (
    // bd-uhi3b — viewport-height flex column so the job list (middle) becomes
    // the scrollable region while the stats/header above and pagination below
    // stay put. Mirrors the Brand Blueprints layout.
    <div className="flex flex-col space-y-3 h-full min-h-0">
      {/* deepcontext-6i0j.1 — the actions row (search/isolation, Est. Daily
          Cost, Create Job) renders inline at the top of the page, matching
          where Brand Blueprints' SearchFilterBar sits: AppLayout's
          page-actions slot is flex-shrink-0 (sized to its content, with an
          empty flex-1 spacer soaking up the rest of the row), so a
          full-width search there can never actually grow. The list already
          reloads on every SSE jobs_update event (useScheduledJobsData), so
          there is no separate Refresh control. */}
      <JobsToolbar
        isolatedJobId={isolatedJobId}
        isolatedJobName={isolatedJob?.name}
        onClearIsolation={clearIsolation}
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
        isAdmin={isAdmin}
        onCreate={form.openCreateForm}
        estimatedDailyCost={stats.estimatedDailyCost}
      />

      {/* Messages */}
      {error && (
        <div className="flex-none bg-risk-bg border border-risk rounded-lg p-4">
          <div className="flex items-center gap-2">
            <XCircleIcon className="h-5 w-5 text-risk" />
            <span className="text-risk">{error}</span>
          </div>
        </div>
      )}

      {success && (
        <div className="flex-none bg-safe-bg border border-safe rounded-lg p-4">
          <div className="flex items-center gap-2">
            <CheckCircleIcon className="h-5 w-5 text-safe" />
            <span className="text-safe">{success}</span>
          </div>
        </div>
      )}

      {/* Jobs card — table-card spec (deepcontext-c0p7.4): shell chrome on
          the card, pagination as a ghost-bg footer INSIDE it (not a
          floating block below), matching Explore / Brand Blueprints. The
          table box scrolls its own body (bd-uhi3b) via the shared
          table-fill contract (bd-n3uyh, deepcontext-wbra). */}
      <div
        className={`${fillOrHugClass(isFullPage)} min-h-0 flex flex-col overflow-hidden rounded-2xl border border-line bg-surface shadow-sm`}
      >
        <TableFillBox isFullPage={isFullPage}>
          {jobs.length === 0 ? (
            <div className="p-8 text-center text-ink-muted">
              No scheduled jobs found. Create your first job to get started.
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="p-8 text-center text-ink-muted">
              {isolatedJobId
                ? "That scheduled job no longer exists."
                : `No jobs match "${debouncedSearchTerm.trim()}".`}
            </div>
          ) : (
            <JobsTable
              jobs={pagedJobs}
              isAdmin={isAdmin}
              localModels={localModelAllowed ? localModels : undefined}
              blueprintNames={blueprintNames}
              isFullPage={isFullPage}
              onShowRuns={runsModal.openRunsModal}
              onEdit={form.startEdit}
              onToggleActive={mutations.toggleActive}
              onExecute={mutations.executeJob}
              onDelete={mutations.deleteJob}
              onDuplicate={form.startDuplicate}
              onGoToBlueprint={goToBlueprint}
            />
          )}
        </TableFillBox>

        {/* Pagination (bd-uhi3b) — hidden when a single page (including the
            isolated-to-one-job case). Wired like Brand Blueprints. */}
        {paginationInfo.totalPages > 1 && (
          <div className="flex-shrink-0 border-t border-line bg-ghost-bg px-4 py-2.5">
            <Pagination
              pagination={paginationInfo}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>

      {/* Create Job Modal */}
      {form.showCreateForm && (
        <ScheduledJobFormModal
          mode="create"
          {...formModalProps}
          onSubmit={handleCreateJob}
          onClose={form.closeCreateModal}
        />
      )}

      {/* Edit Job Modal */}
      {form.showEditForm && form.editingJob && (
        <ScheduledJobFormModal
          mode="edit"
          job={form.editingJob}
          {...formModalProps}
          onSubmit={handleUpdateJob}
          onClose={form.closeEditModal}
        />
      )}

      {/* Duplicate Job Modal (deepcontext-c919.2) */}
      {form.showDuplicateForm && form.duplicateSourceJob && (
        <ScheduledJobFormModal
          mode="duplicate"
          job={form.duplicateSourceJob}
          {...formModalProps}
          onSubmit={handleDuplicateJob}
          onClose={form.closeDuplicateModal}
        />
      )}

      {/* Job Runs Modal — extracted to ../jobRuns/JobRunsModal (bd-hfv7u.3). */}
      {runsModal.showRunsModal && runsModal.selectedJob && (
        <JobRunsModal
          jobName={runsModal.selectedJob.name}
          jobId={runsModal.selectedJob.id}
          runs={runsModal.jobRuns}
          urlsPerRun={runsModal.selectedJob.urlsPerRun}
          // bd-hfv7u.6 — lazy-loader for older history pages via the cursor
          // endpoint. The modal owns its own windowing/merge over these.
          fetchRunsPage={(cursor) =>
            scheduledJobsApi.getJobRunsPage(runsModal.selectedJob!.id, cursor)
          }
          // bd-hfv7u.7 — aggregate over the full history for the KPI strip.
          fetchAggregate={() =>
            scheduledJobsApi.getJobRunsAggregate(runsModal.selectedJob!.id)
          }
          startDate={runsModal.selectedJob.startDate}
          endDate={runsModal.selectedJob.endDate}
          lastRunAt={runsModal.selectedJob.lastRunAt}
          nextRunAt={runsModal.selectedJob.nextRunAt}
          shortDate={shortDate}
          rel={rel}
          relFuture={relFuture}
          isLive={runsModal.isLive}
          onClose={runsModal.closeRunsModal}
          expandedRunId={runsModal.expandedRunId}
          unmatchedSort={runsModal.unmatchedSort}
          unmatchedUrls={runsModal.unmatchedUrls}
          unmatchedLoading={runsModal.unmatchedLoading}
          unmatchedError={runsModal.unmatchedError}
          onCancelRun={runsModal.cancelRun}
          onToggleDrilldown={runsModal.toggleDrilldown}
          onChangeUnmatchedSort={runsModal.changeUnmatchedSort}
          formatDate={formatDate}
          calculateDuration={calculateDuration}
          getRunStatusColor={getRunStatusColor}
          getRunStatusIcon={getRunStatusIcon}
        />
      )}

      {/* Site List Modal */}
      <SiteListModal
        isOpen={showSiteListModal}
        onClose={() => setShowSiteListModal(false)}
        onSelect={form.applyDomainSelection}
        initialSelectedDomains={form.selectedDomainInfos}
      />
    </div>
  );
};

export default ScheduledJobsPage;
